/**
 * Multi-unit conversion utilities.
 * Products can have a `units_per_pack` multiplier and `unit` label.
 * This utility formats quantities with both pack and individual unit counts.
 */

/** Fraction of par at which stock is considered "low". Used across Dashboard, Inventory, StoreDetail, Reorder, etc. */
export const LOW_STOCK_THRESHOLD = 0.25;

export type ProductLike = {
  unit?: string | null;
  pack_size?: string | null;
  units_per_pack?: number | null;
  // 3-level pack fields
  sub_unit?: string | null;
  sub_units_per_pack?: number | null;
  units_per_sub_unit?: number | null;
};

export type CountingUnit = "each" | "cases";

/**
 * Check if a product supports case counting (has units_per_pack > 1).
 */
export function hasCaseUnit(product: ProductLike | null | undefined): boolean {
  return !!product?.units_per_pack && product.units_per_pack > 1;
}

/**
 * Convert a user-entered quantity to eaches based on counting unit.
 * If counting in cases, multiplies by units_per_pack.
 * If counting in eaches (or no conversion applies), returns qty as-is.
 */
export function convertToEaches(
  qty: number,
  countingUnit: CountingUnit,
  product: ProductLike | null | undefined
): number {
  if (countingUnit === "cases" && product?.units_per_pack && product.units_per_pack > 1) {
    return qty * product.units_per_pack;
  }
  return qty;
}

/**
 * Convert eaches back to case count for display.
 */
export function eachesToCases(
  eaches: number,
  product: ProductLike | null | undefined
): number | null {
  if (!product?.units_per_pack || product.units_per_pack <= 1) return null;
  return eaches / product.units_per_pack;
}

/**
 * Get the individual unit total, or null if no conversion applies.
 */
export function getIndividualUnits(
  qty: number,
  product: ProductLike | null | undefined
): number | null {
  if (!product?.units_per_pack || product.units_per_pack <= 1) return null;
  return qty * product.units_per_pack;
}

/**
 * Decompose a total eaches value into cases + loose remainder.
 * Useful for pre-filling the stock calculator when editing.
 */
export function decompose(
  totalEaches: number,
  product: ProductLike | null | undefined
): { cases: number; loose: number } {
  if (!product?.units_per_pack || product.units_per_pack <= 1) {
    return { cases: 0, loose: totalEaches };
  }
  const upp = product.units_per_pack;
  const intCases = Math.floor(totalEaches / upp);
  const remainder = totalEaches - intCases * upp;
  // Snap near-integer remainders to avoid floating-point drift (e.g. 16.999999 → 17)
  const loose = Math.abs(remainder - Math.round(remainder)) < 1e-9
    ? Math.round(remainder)
    : remainder;
  // If loose is fractional, the value came from a fractional-case entry (e.g. 2.7 cases).
  // Return as fractional cases with 0 loose so the user's input round-trips cleanly.
  if (loose !== 0 && !Number.isInteger(loose)) {
    return { cases: parseFloat((totalEaches / upp).toFixed(4)), loose: 0 };
  }
  return { cases: intCases, loose };
}

/**
 * Compose cases + loose into total eaches.
 */
export function compose(
  cases: number,
  loose: number,
  product: ProductLike | null | undefined
): number {
  const unitsPerPack = product?.units_per_pack && product.units_per_pack > 1 ? product.units_per_pack : 1;
  return cases * unitsPerPack + loose;
}

/**
 * Get a short unit label for display.
 */
export function getUnitLabel(product: ProductLike | null | undefined): string {
  if (!product) return "";
  return product.unit || "";
}

/**
 * Get the pack/case label for a product.
 */
export function getCaseLabel(product: ProductLike | null | undefined): string {
  if (!product) return "";
  return product.unit || product.pack_size || "";
}

/**
 * Round a calculated order quantity to the nearest whole number.
 * Any decimal ≥ .5 rounds up; < .5 rounds down. (Standard Math.round.)
 * Apply to par-based calculations before displaying or submitting orders.
 */
export function roundOrderQty(qty: number): number {
  if (!Number.isFinite(qty)) return 0;
  return Math.max(0, Math.round(qty));
}

/**
 * Calculate par-based order quantity in Unit 1 (cases/boxes/etc).
 *
 * `parUnit1` is the par level in Unit 1 (e.g., 2 = 2 cases). Stored directly in DB.
 * `qtyEaches` is the on-hand quantity in total eaches (as stored in DB).
 * Returns the number of Unit 1 to order (rounded to nearest whole, 0.5 rounds up).
 *
 * Examples (units_per_pack=500, par=2 cases):
 *   qty=250 eaches → 0.5 cases on hand → deficit 1.5 → round=2 → order 2 cases
 *   qty=800 eaches → 1.6 cases on hand → deficit 0.4 → round=0 → no order
 *   qty=750 eaches → 1.5 cases on hand → deficit 0.5 → round=1 → order 1 case
 *
 * For single-unit products (no units_per_pack), par and qty are both in eaches.
 */
export function calcParOrderQty(
  parUnit1: number,
  qtyEaches: number,
  product: ProductLike | null | undefined
): number {
  if (!Number.isFinite(parUnit1) || !Number.isFinite(qtyEaches)) return 0;
  const qtyUnit1 = toUnit1(qtyEaches, product);
  if (parUnit1 <= qtyUnit1) return 0;
  const deficit = parUnit1 - qtyUnit1;
  return Math.max(0, Math.round(deficit));
}

/**
 * Calculate order quantity for a min_order reorder method.
 *
 * All inputs in Unit 1 except qtyEaches (which is converted via toUnit1).
 * Rule: if on-hand (Unit 1) <= threshold (Unit 1), order amount (Unit 1).
 * Otherwise, order 0.
 *
 * Examples:
 *   threshold=2, amount=5, qty=24 eaches, units_per_pack=24 → 1 case on hand → 1 <= 2 → order 5 cases
 *   threshold=2, amount=5, qty=72 eaches, units_per_pack=24 → 3 cases on hand → 3 > 2 → no order
 *
 * For single-unit products, threshold/amount/qty are all in eaches.
 */
export function calcMinOrderQty(
  thresholdUnit1: number | null | undefined,
  amountUnit1: number | null | undefined,
  qtyEaches: number,
  product: ProductLike | null | undefined,
): number {
  if (thresholdUnit1 == null || amountUnit1 == null) return 0;
  if (!Number.isFinite(thresholdUnit1) || !Number.isFinite(amountUnit1) || !Number.isFinite(qtyEaches)) return 0;
  const qtyUnit1 = toUnit1(qtyEaches, product);
  return qtyUnit1 <= thresholdUnit1 ? amountUnit1 : 0;
}

/**
 * Compute the suggested order quantity in Unit 1 for a store_products row.
 *
 * Single source of truth used by Reorder, Inventory Today, MarkOrderedDialog,
 * and any other surface that needs to know "how much should we order?"
 *
 * Rules (in order):
 *   1. Lenient par-config: if `par_level` is set, use par-based math regardless of
 *      `reorder_method`. Falls back to `min_order` math when `reorder_method='min_order'`
 *      AND `min_order_threshold/amount` are set AND `par_level` is null.
 *   2. `max_level` ceiling: don't suggest more than fits (cap at `max_level - on_hand`).
 *
 * `quantity_on_hand` is in eaches; everything else is in Unit 1.
 */
export function calcSuggestedOrderQty(
  sp: {
    quantity_on_hand: number;
    par_level: number | null;
    min_order_threshold: number | null;
    min_order_amount: number | null;
    max_level?: number | null;
    reorder_method: "par_level" | "min_order";
    products?: ProductLike | null;
  },
): number {
  const product = sp.products ?? null;
  const isMinOrder = sp.reorder_method === "min_order";
  const hasPar = sp.par_level != null;

  let qty = isMinOrder
    ? calcMinOrderQty(sp.min_order_threshold, sp.min_order_amount, sp.quantity_on_hand, product)
    : hasPar
      ? calcParOrderQty(sp.par_level!, sp.quantity_on_hand, product)
      : 0;

  // max_level ceiling (Unit 1)
  if (qty > 0 && sp.max_level != null) {
    const qtyUnit1 = toUnit1(sp.quantity_on_hand, product);
    const headroomUnit1 = Math.max(0, sp.max_level - qtyUnit1);
    qty = Math.min(qty, headroomUnit1);
  }

  return qty;
}

/**
 * Round order quantity to nearest whole Unit 1 (in eaches).
 * e.g. orderQty=19, units_per_pack=12 → 19/12=1.58 → round=2 → 24 eaches
 * Returns qty unchanged if product has no case unit.
 */
export function ceilToCases(
  orderQty: number,
  product: ProductLike | null | undefined
): number {
  if (!hasCaseUnit(product)) return orderQty;
  const caseSize = product!.units_per_pack!;
  return Math.round(orderQty / caseSize) * caseSize;
}

/**
 * Format an order quantity as rounded Unit 1, for Order Guide / Inventory Today.
 * e.g. orderQty=19, units_per_pack=12 → "2 cases"
 * Falls back to raw number if no case unit is set.
 */
export function formatCeilInCases(
  orderQty: number,
  product: ProductLike | null | undefined
): string {
  if (!hasCaseUnit(product)) return `${orderQty}`;
  const caseSize = product!.units_per_pack!;
  const cases = Math.round(orderQty / caseSize);
  const label = product!.unit || "";
  return label ? `${cases} ${label}` : `${cases}`;
}

// ─── Unit 1 conversion helpers ────────────────────────────────────────────────

/**
 * Convert total eaches to Unit 1 equivalent (decimal).
 * e.g. 750 eaches with 500 per case → 1.5 Cases
 */
export function toUnit1(
  eaches: number,
  product: ProductLike | null | undefined
): number {
  if (!product?.units_per_pack || product.units_per_pack <= 1) return eaches;
  return eaches / product.units_per_pack;
}

/**
 * Format eaches as a Unit 1 display string.
 * e.g. 750 → "1.5 Cases", 500 → "1 Case", 7 → "7" (no unit)
 */
export function formatAsUnit1(
  eaches: number,
  product: ProductLike | null | undefined
): string {
  if (!product?.units_per_pack || product.units_per_pack <= 1) {
    const label = product?.unit || "";
    return label ? `${eaches} ${label}` : eaches.toString();
  }
  const unit1Val = eaches / product.units_per_pack;
  const label = product.unit || "";
  const rounded2 = parseFloat(unit1Val.toFixed(2));
  // If 2dp rounds to 0 but the real value is non-zero (e.g. 0.0042), show up to 4dp
  // so the list displays the true quantity instead of a misleading "0".
  const formatted = (rounded2 === 0 && unit1Val > 0)
    ? parseFloat(unit1Val.toFixed(4)).toString()
    : rounded2.toString();
  return label ? `${formatted} ${label}` : formatted;
}

/**
 * Format a par level for display. Par is stored in Unit 1 (e.g., 2 = 2 cases).
 * Just appends the unit label — does NOT divide by units_per_pack.
 */
export function formatPar(
  parUnit1: number,
  product: ProductLike | null | undefined
): string {
  if (!product?.units_per_pack || product.units_per_pack <= 1) {
    const label = product?.unit || "";
    const formatted = parseFloat(parUnit1.toFixed(2)).toString();
    return label ? `${formatted} ${label}` : formatted;
  }
  const label = product.unit || "";
  const formatted = parseFloat(parUnit1.toFixed(2)).toString();
  return label ? `${formatted} ${label}` : formatted;
}

/**
 * Get the number of tiers configured for a product.
 * 1 = single unit (no pack), 2 = unit + each, 3 = unit + sub-unit + each
 */
export function getTierCount(product: ProductLike | null | undefined): 1 | 2 | 3 {
  if (!product?.units_per_pack || product.units_per_pack <= 1) return 1;
  if (hasSubUnit(product)) return 3;
  return 2;
}

/**
 * Get labels for each tier.
 * unit3 is the "loose" label used in TwoTierInput — shows sub_unit name when set
 * (e.g. "Sleeve" for a Case→Sleeve 2-tier product), otherwise falls back to "each".
 */
export function getTierLabels(product: ProductLike | null | undefined): {
  unit1: string;
  unit2: string | null;
  unit3: string;
} {
  return {
    unit1: product?.unit || "",
    unit2: hasSubUnit(product) ? (product?.sub_unit || "") : null,
    // For 2-tier named products (Case→Sleeve with no Unit 3), use sub_unit as the loose label
    unit3: (!hasSubUnit(product) && product?.sub_unit) ? product.sub_unit : "each",
  };
}

// ─── 3-Level Pack Helpers ─────────────────────────────────────────────────────

/**
 * Check if a product has a mid-level sub-unit (3-level: outer → mid → each).
 */
export function hasSubUnit(product: ProductLike | null | undefined): boolean {
  return (
    !!product?.sub_unit &&
    !!product?.sub_units_per_pack &&
    product.sub_units_per_pack > 0 &&
    !!product?.units_per_sub_unit &&
    product.units_per_sub_unit > 0
  );
}

/**
 * Get the sub-unit label (e.g. "sleeve", "bag").
 */
export function getSubUnitLabel(product: ProductLike | null | undefined): string {
  return product?.sub_unit || "";
}

/**
 * Compose outers + mids + loose eaches into total eaches (3-level).
 * If the product has no sub-unit, falls back to 2-level compose (outers as cases).
 */
export function compose3(
  outers: number,
  mids: number,
  loose: number,
  product: ProductLike | null | undefined
): number {
  if (hasSubUnit(product)) {
    const subPerPack = product!.sub_units_per_pack!;
    const eachPerSub = product!.units_per_sub_unit!;
    return outers * subPerPack * eachPerSub + mids * eachPerSub + loose;
  }
  // Fall back to 2-level
  return compose(outers, loose + mids, product);
}

// ─── Price helpers ───────────────────────────────────────────────────────────

type PricedProduct = ProductLike & { price?: number | null };

/**
 * Return the price per individual each.
 * `casePrice` is per Unit1 (what the vendor charges per case/box/etc).
 * For single-unit products (units_per_pack null or ≤1) the case price IS the each price.
 */
export function getPricePerEach(
  casePrice: number,
  product: PricedProduct | null | undefined
): number {
  const upp = product?.units_per_pack && product.units_per_pack > 1 ? product.units_per_pack : 1;
  return casePrice / upp;
}

/**
 * Total cost of wasted/moved quantity given the catalog (case-level) price.
 * qty_eaches × (casePrice / units_per_pack)
 */
export function calcWasteCost(
  qtyEaches: number,
  casePrice: number,
  product: PricedProduct | null | undefined
): number {
  return qtyEaches * getPricePerEach(casePrice, product);
}

/**
 * Dollar value of on-hand stock.
 * Converts eaches to Unit1, then multiplies by the Unit1 (case) price.
 */
export function calcStockValue(
  qtyEaches: number,
  casePrice: number,
  product: PricedProduct | null | undefined
): number {
  return toUnit1(qtyEaches, product) * casePrice;
}

/**
 * Decompose total eaches into { outers, mids, loose } (3-level).
 * If product has no sub-unit, outers and mids will be 0.
 */
export function decompose3(
  totalEaches: number,
  product: ProductLike | null | undefined
): { outers: number; mids: number; loose: number } {
  if (!hasSubUnit(product)) {
    const { cases, loose } = decompose(totalEaches, product);
    return { outers: cases, mids: 0, loose };
  }
  const subPerPack = product!.sub_units_per_pack!;
  const eachPerSub = product!.units_per_sub_unit!;
  const outerSize = subPerPack * eachPerSub;
  const outers = Math.floor(totalEaches / outerSize);
  const rem = totalEaches % outerSize;
  const mids = Math.floor(rem / eachPerSub);
  const loose = rem % eachPerSub;
  return { outers, mids, loose };
}
